import axios from "axios";
import type { AxiosInstance, AxiosRequestConfig, InternalAxiosRequestConfig } from "axios";
import { JSON_HTTP_HEADERS } from "@/services/httpHeaders";
import { createBearerAuthHeaders } from "@/services/httpHeaders";
import { getRealmApiUrl } from "@/constants/navigation";
import {
  getRealmAccessToken,
  clearRealmAuthTokens,
} from "@/services/realmTokenStorage";
import { refreshRealmAccessToken } from "@/services/realmAuthRefreshSession";

type RetryConfig = InternalAxiosRequestConfig & { _retry?: boolean };

export function createRealmApiClient(
  domain: string,
  realmUuid: string,
  onUnauthorized?: () => void,
): AxiosInstance {
  const baseURL = getRealmApiUrl(domain, "");

  const client = axios.create({
    baseURL,
    headers: JSON_HTTP_HEADERS,
  });

  client.interceptors.request.use((config) => {
    const token = getRealmAccessToken(realmUuid);
    if (token) {
      config.headers = config.headers ?? {};
      Object.assign(config.headers, createBearerAuthHeaders(token));
    }
    return config;
  });

  client.interceptors.response.use(
    (response) => response,
    async (error: unknown) => {
      if (!axios.isAxiosError(error)) return Promise.reject(error);

      const status = error.response?.status;
      if (status === 401) {
        const original = error.config as RetryConfig | undefined;
        if (original && !original._retry) {
          original._retry = true;

          const newToken = await refreshRealmAccessToken(realmUuid, domain);
          if (newToken) {
            original.headers = original.headers ?? {};
            Object.assign(original.headers, createBearerAuthHeaders(newToken));
            return client.request(original as AxiosRequestConfig);
          }

          clearRealmAuthTokens(realmUuid);
          onUnauthorized?.();
        }
      }

      return Promise.reject(error);
    },
  );

  return client;
}